<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router';
import SettingsPageTitle from '@/settingsModule/components/SettingsPageTitle.vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';
import { useOrderTemplateForm } from '@/orderTemplateModule/composables/useOrderTemplateForm';
import { i18n } from '@/i18n';

const route = useRoute();
const router = useRouter();
const t = i18n.t;

const {
  fileOriginalName,
  formMessage,
  formMessageType,
  initializing,
  hasUploadInfo,
  isBusy,
  fieldState,
  mappingStates,
  maxOrdersFieldName,
  buildMappingFieldName,
  handleMaxOrdersPerSheetInput,
  handleToggleField,
  handleStartCellInput,
  submit,
  cancel,
} = useOrderTemplateForm({ route, router });
</script>

<template>
  <section class="order-template-create">
    <header class="order-template-create__header">
      <SettingsPageTitle :label="t('設定 > 書式・帳票 > 書式設定')" />
    </header>

    <div class="order-template-create__content">
      <div
        v-if="formMessage && formMessageType !== 'error'"
        :class="['order-template-create__message', { 'order-template-create__message--error': formMessageType === 'error' }]"
      >
        {{ formMessage }}
      </div>

      <div v-if="initializing" class="order-template-create__loading">
        {{ t('テンプレート情報を読み込んでいます…') }}
      </div>

      <div v-else-if="!hasUploadInfo" class="order-template-create__warning">
        {{ t('先に書式ファイルをアップロードしてからこのページを開いてください。') }}
      </div>

      <form v-else class="order-template-create__card" @submit.prevent="submit">
        <header class="order-template-create__card-header">
          <h1 class="order-template-create__heading">
            {{ t('項目とセルのマッピング') }}
          </h1>
          <p class="order-template-create__subtitle">
            {{
              t('各データ項目をExcelのどのセルから出力するか指定してください。複数出力する項目は「複数出力」にチェックを入れてください。')
            }}
          </p>
        </header>
        <section class="order-template-create__section">
          <div class="order-template-create__meta">
            <div class="order-template-create__field-row">
              <span class="order-template-create__field-label">{{ t('ファイル名') }}</span>
              <div class="order-template-create__field-control">
                <input v-model="fileOriginalName" class="order-template-create__input" readonly />
              </div>
            </div>
            <div class="order-template-create__field-row order-template-create__field-row--number">
              <span class="order-template-create__field-label">
                {{ t('1シートあたりの出力上限数') }}
              </span>
              <div class="order-template-create__field-control">
                <div class="order-template-create__number">
                  <input
                    :value="fieldState[maxOrdersFieldName].value.value"
                    class="order-template-create__input order-template-create__input--number"
                    type="number"
                    min="1"
                    max="10000"
                    :disabled="isBusy"
                    @input="handleMaxOrdersPerSheetInput(($event.target as HTMLInputElement).value)"
                  />
                  <span class="order-template-create__number-unit">{{ t('個') }}</span>
                </div>
                <p v-if="fieldState[maxOrdersFieldName].isError.value" class="order-template-create__field-error">
                  <Mdi class="order-template-create__field-error-icon" icon="error_outline" />
                  <span>{{ fieldState[maxOrdersFieldName].errorMessage.value }}</span>
                </p>
              </div>
            </div>
          </div>
          <p class="order-template-create__note">
            {{
              t(
                '設定例: 商品名をA1に設定し、1シートあたりの出力上限数を20個とした場合、A1からA20まで出力されます。21個目以降は2つ目のファイルとして出力されます。'
              )
            }}
          </p>
        </section>

        <section class="order-template-create__mapping">
          <div class="order-template-create__mapping-header">
            <span>{{ t('出力項目') }}</span>
            <span>{{ t('項目名') }}</span>
            <span>{{ t('出力開始セル') }}</span>
          </div>
          <div class="order-template-create__mapping-body">
            <div v-for="(field, index) in mappingStates" :key="field.fieldKey" class="order-template-create__mapping-row">
              <div class="order-template-create__mapping-checkbox">
                <input
                  :id="`mapping-field-${field.fieldKey}`"
                  type="checkbox"
                  class="order-template-create__checkbox"
                  :checked="field.enabled"
                  :disabled="isBusy"
                  @change="handleToggleField(index, ($event.target as HTMLInputElement).checked)"
                />
              </div>
              <label class="order-template-create__mapping-label" :for="`mapping-field-${field.fieldKey}`">
                {{ t(field.label) }}
              </label>
              <div class="order-template-create__mapping-cell">
                <input
                  :value="field.startCell"
                  class="order-template-create__input"
                  type="text"
                  :placeholder="t('例: A1')"
                  maxlength="6"
                  :disabled="!field.enabled || isBusy"
                  @input="handleStartCellInput(index, ($event.target as HTMLInputElement).value)"
                />
                <p v-if="fieldState[buildMappingFieldName(field.fieldKey)].isError.value" class="order-template-create__field-error">
                  <Mdi class="order-template-create__field-error-icon" icon="error_outline" />
                  <span>
                    {{ fieldState[buildMappingFieldName(field.fieldKey)].errorMessage.value }}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        <div class="order-template-create__actions">
          <div v-if="formMessage && formMessageType === 'error'" class="order-template-create__form-error">
            <Mdi class="order-template-create__form-error-icon" icon="error_outline" />
            <span>{{ formMessage }}</span>
          </div>
          <div class="order-template-create__action-buttons">
            <button class="order-template-create__primary" type="submit" :disabled="isBusy">
              {{ isBusy ? t('保存中…') : t('テンプレートを保存') }}
            </button>
            <button class="order-template-create__secondary" type="button" :disabled="isBusy" @click="cancel">
              {{ t('キャンセル') }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </section>
</template>

<style scoped lang="scss">
.order-template-create {
  display: grid;
  gap: 24px;
  padding: 24px;

  &__header {
    display: grid;
    gap: 8px;
  }

  &__content {
    display: grid;
    gap: 24px;
    max-width: 800px;
    width: 100%;
    margin: 0;
  }

  &__heading {
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    font-family: 'Noto Sans JP', sans-serif;
    color: $textDefault;
  }

  &__subtitle {
    margin: 0;
    font-size: 14px;
    color: $textSecondary;
  }

  &__message {
    padding: 12px 16px;
    border-radius: 4px;
    background-color: $surfaceDefault;
    color: $textSecondary;

    &--error {
      background-color: $semanticErrorSubdued;
      color: $semanticError;
    }
  }

  &__warning {
    padding: 16px;
    border-radius: 4px;
    background-color: rgba($semanticWarning, 0.12);
    color: $semanticWarning;
  }

  &__loading {
    padding: 16px;
    border-radius: 4px;
    background-color: $surfaceDefault;
    color: $textSecondary;
    font-size: 14px;
  }

  &__card {
    display: grid;
    gap: 16px;
    padding: 16px;
    border-radius: 4px;
    border: 1px solid $borderDefault;
    background-color: $semanticWhite;
    width: 100%;
  }

  &__section {
    display: grid;
    gap: 16px;
  }

  &__card-header {
    display: grid;
    gap: 8px;
  }

  &__meta {
    display: grid;
    gap: 16px;
  }

  &__field-row {
    display: grid;
    grid-template-columns: 210px minmax(0, 1fr);
    align-items: center;
    column-gap: 24px;

    @media (max-width: 640px) {
      grid-template-columns: 1fr;
      row-gap: 8px;
    }
  }

  &__field-control {
    display: grid;
    gap: 8px;
  }

  &__field-label {
    font-size: 16px;
    font-weight: 400;
    color: $textSecondary;
    font-family: 'Noto Sans JP', sans-serif;
  }

  &__input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid $borderDefault;
    border-radius: 4px;
    font-size: 14px;
    color: $textSecondary;
    font-weight: 400;
    font-family: 'Noto Sans JP', sans-serif;
    line-height: 1.3;
    background-color: $semanticWhite;

    &::placeholder {
      color: $textDisable;
      font-family: 'Noto Sans JP', sans-serif;
      font-size: 14px;
      line-height: 1.3;
    }

    &:disabled {
      background-color: $surfaceDefault;
      border-color: $borderSecondary;
      color: $textDisable;
      cursor: not-allowed;
    }
  }

  &__input--number {
    text-align: right;
  }

  &__number {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    align-items: center;
  }

  &__number-unit {
    font-size: 16px;
    color: $textSecondary;
    font-family: 'Noto Sans JP', sans-serif;
  }

  &__note {
    margin: 0;
    padding: 12px 16px;
    border-radius: 4px;
    background-color: $backgroundDefault;
    color: $textSecondary;
    font-size: 16px;
    font-family: 'Noto Sans JP', sans-serif;
    font-weight: 400;
  }

  &__mapping {
    display: grid;
    gap: 8px;
  }

  &__mapping-header {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr) 220px;
    gap: 8px;
    font-size: 13px;
    font-weight: 600;
    color: $textSecondary;
  }

  &__mapping-body {
    display: grid;
    gap: 8px;
    padding: 8px 0;
    border-top: 1px solid $borderDefault;
    border-bottom: 1px solid $borderDefault;
  }

  &__mapping-row {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr) 220px;
    gap: 8px;
    align-items: center;
  }

  &__checkbox {
    width: 16px;
    height: 16px;
  }

  &__mapping-label {
    font-size: 14px;
    color: $textDefault;
    display: flex;
    align-items: center;
  }

  &__mapping-cell {
    display: grid;
    gap: 6px;
  }

  &__mapping-checkbox {
    display: grid;
    align-items: center;
    justify-items: start;
  }

  &__field-error {
    margin: 0;
    font-size: 12px;
    color: $semanticError;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__field-error-icon {
    font-size: 16px;
  }

  &__actions {
    display: grid;
    gap: 16px;
    justify-items: center;
  }

  &__action-buttons {
    display: grid;
    grid-auto-flow: column;
    gap: 16px;
    justify-content: center;
    justify-items: center;
    width: 100%;
  }

  &__form-error {
    width: 100%;
    display: grid;
    grid-auto-flow: column;
    justify-content: start;
    align-items: center;
    gap: 8px;
    padding: 16px;
    border-radius: 8px;
    background-color: rgba($semanticError, 0.08);
    color: $semanticError;
    font-family: 'Noto Sans JP', sans-serif;
    font-size: 14px;
  }

  &__form-error-icon {
    font-size: 20px;
  }

  &__primary,
  &__secondary {
    padding: 12px 16px;
    border-radius: 4px;
    border: 1px solid transparent;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s ease;
  }

  &__primary {
    background-color: $semanticPrimary;
    color: $semanticWhite;
  }

  &__secondary {
    background-color: $semanticWhite;
    color: $textDefault;
    border-color: $borderDefault;
  }

  &__primary:disabled,
  &__secondary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
